import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { 
  History, 
  TrendingUp, 
  Search,
  Clock,
  X,
  Zap
} from 'lucide-react'
import { SearchSuggestion } from '@/hooks/useGlobalSearch'
import { useAccessibleMotion } from '@/hooks/useAccessibleMotion'

interface SearchSuggestionsProps {
  suggestions: SearchSuggestion[]
  recentSearches: string[]
  onSuggestionClick: (suggestion: string) => void
  onClearRecent: () => void
  className?: string
  query: string
}

const SuggestionItem: React.FC<{
  suggestion: SearchSuggestion
  onClick: () => void
  index: number
}> = ({ suggestion, onClick, index }) => {
  const { prefersReducedMotion } = useAccessibleMotion()
  
  const getIcon = () => {
    switch (suggestion.type) {
      case 'recent': return History
      case 'popular': return TrendingUp
      case 'suggestion': return Search
      default: return Search
    }
  }

  const getIconColor = () => {
    switch (suggestion.type) {
      case 'recent': return 'text-blue-500'
      case 'popular': return 'text-green-500'
      case 'suggestion': return 'text-purple-500'
      default: return 'text-gray-500'
    }
  }

  const Icon = getIcon()

  return (
    <motion.div
      initial={{ opacity: 0, x: prefersReducedMotion ? 0 : -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ 
        duration: prefersReducedMotion ? 0.01 : 0.15, 
        delay: prefersReducedMotion ? 0 : index * 0.03 
      }}
    >
      <Button
        variant="ghost"
        className="w-full h-auto p-3 justify-start text-left hover:bg-muted/50 transition-colors"
        onClick={onClick}
      >
        <div className="flex items-center gap-3 w-full">
          <Icon className={`w-4 h-4 flex-shrink-0 ${getIconColor()}`} />
          <span className="text-sm text-foreground flex-1 truncate">
            {suggestion.query}
          </span>
          {suggestion.count && (
            <Badge variant="outline" className="text-xs">
              {suggestion.count}
            </Badge>
          )}
        </div>
      </Button>
    </motion.div>
  )
}

const PopularSearches: React.FC<{
  onSearchClick: (query: string) => void
}> = ({ onSearchClick }) => {
  const popularSearches = [
    'pending leads',
    'approved galleries',
    'published services',
    'social posts today',
    'new reviews',
    'draft content'
  ]

  return (
    <div className="p-4 border-b border-border">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp className="w-4 h-4 text-green-500" />
        <span className="text-sm font-medium">Popular Searches</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {popularSearches.map((search) => (
          <Button
            key={search}
            variant="outline"
            size="sm"
            className="h-7 text-xs hover:bg-primary/10 hover:border-primary/30"
            onClick={() => onSearchClick(search)}
          >
            {search}
          </Button>
        ))}
      </div>
    </div>
  )
}

const EmptyState: React.FC<{
  onPopularSearchClick: (query: string) => void
}> = ({ onPopularSearchClick }) => (
  <div className="text-center py-8 px-4">
    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
      <Search className="w-8 h-8 text-primary" />
    </div>
    <h3 className="text-lg font-medium mb-2">Start searching</h3>
    <p className="text-sm text-muted-foreground mb-6">
      Search across leads, galleries, services, content, and more
    </p>
    <PopularSearches onSearchClick={onPopularSearchClick} />
    <div className="mt-6 text-xs text-muted-foreground">
      <p className="mb-2">Search tips:</p>
      <div className="text-left max-w-xs mx-auto space-y-1">
        <p>• Use keywords like "pending", "approved", "published"</p>
        <p>• Search by name, email, phone for leads</p>
        <p>• Find content by title or description</p>
        <p>• Use Cmd+K (Mac) or Ctrl+K (Windows) for quick search</p>
      </div>
    </div>
  </div>
)

export const SearchSuggestions: React.FC<SearchSuggestionsProps> = ({
  suggestions,
  recentSearches,
  onSuggestionClick,
  onClearRecent,
  className = '',
  query
}) => {
  const { prefersReducedMotion } = useAccessibleMotion()

  // Show empty state when no query and no recent searches
  if (!query && recentSearches.length === 0) {
    return (
      <div className={`bg-card border border-border rounded-lg shadow-lg ${className}`}>
        <EmptyState onPopularSearchClick={onSuggestionClick} />
      </div>
    )
  }

  return (
    <div className={`bg-card border border-border rounded-lg shadow-lg ${className}`}>
      <AnimatePresence mode="wait">
        {/* Recent searches section */}
        {!query && recentSearches.length > 0 && (
          <motion.div
            key="recent-searches"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: prefersReducedMotion ? 0.01 : 0.2 }}
          >
            <div className="p-4 border-b border-border">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-500" />
                  <span className="text-sm font-medium">Recent Searches</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs text-muted-foreground hover:text-foreground"
                  onClick={onClearRecent}
                >
                  <X className="w-3 h-3 mr-1" />
                  Clear
                </Button>
              </div>
              <div className="space-y-1">
                {recentSearches.slice(0, 5).map((search, index) => (
                  <SuggestionItem
                    key={search}
                    suggestion={{ query: search, type: 'recent' }}
                    onClick={() => onSuggestionClick(search)}
                    index={index}
                  />
                ))}
              </div>
            </div>
            <PopularSearches onSearchClick={onSuggestionClick} />
          </motion.div>
        )}

        {/* Query-based suggestions */}
        {query && suggestions.length > 0 && (
          <motion.div
            key="suggestions"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: prefersReducedMotion ? 0.01 : 0.2 }}
            className="divide-y divide-border"
          >
            {suggestions.map((suggestion, index) => (
              <SuggestionItem
                key={`${suggestion.type}-${suggestion.query}`}
                suggestion={suggestion}
                onClick={() => onSuggestionClick(suggestion.query)}
                index={index}
              />
            ))}
          </motion.div>
        )}

        {/* No suggestions state */}
        {query && suggestions.length === 0 && (
          <motion.div
            key="no-suggestions"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: prefersReducedMotion ? 0.01 : 0.2 }}
            className="p-6 text-center"
          >
            <Zap className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              No suggestions available for "{query}"
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default SearchSuggestions